'use client';
import React, { useEffect, useRef } from 'react';

const steps = [
  {
    placeholderLabel: 'Field deployment · May 2026',
    gradientStyle: 'linear-gradient(135deg, #1B4332 0%, #2D6A4F 40%, #52796F 100%)',
    imageSrc: '/assets/images/CleanShot_2026-05-29_at_13.01.06_2x-1780077676303.png',
    label: 'YEAR ONE — BASELINE',
    title: 'Ten students map eleven parks',
    body: 'Across eleven days in May 2026, the team logged 853 observations of 222 species, each photographed and submitted to iNaturalist, where a global community of naturalists independently verifies every identification. That community verification is what makes a youth-led survey scientifically credible.',
  },
  {
    placeholderLabel: 'Analysis & partnerships · 2026–27',
    gradientStyle: 'linear-gradient(135deg, #3A5A40 0%, #588157 40%, #A3B18A 100%)',
    imageSrc: '/assets/images/CleanShot_2026-05-29_at_10.57.51_2x-1780070300446.png',
    label: 'THE INTERVAL',
    title: 'A year of analysis and engagement',
    body: 'Between field seasons, the data is analyzed for habitat patterns, invasive pressure, and native canopy health — and shared with municipal partners across five cities to inform local conservation decisions.',
  },
  {
    placeholderLabel: 'Replication · May 2027',
    gradientStyle: 'linear-gradient(135deg, #2D6A4F 0%, #40916C 40%, #74C69D 100%)',
    imageSrc: '/assets/images/CleanShot_2026-05-29_at_10.56.18_2x-1780070204666.png',
    label: 'YEAR TWO — REPLICATION',
    title: 'The same parks, surveyed again',
    body: 'In May 2027 the team re-surveys the same eleven parks with the same method. The year-over-year delta — what appeared, what vanished, what spread — is the project\'s core scientific contribution: a measured picture of ecological change in urban North Texas.',
  },
];

const HowItWorksSection: React.FC = () => {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const cards = entry.target.querySelectorAll<HTMLElement>('.how-card');
            cards.forEach((card, i) => {
              setTimeout(() => {
                card.style.opacity = '1';
                card.style.transform = 'translateY(0)';
              }, i * 180);
            });
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="how-it-works"
      ref={sectionRef}
      className="py-20 md:py-28 px-6 bg-satellite topo-bg">
      
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="max-w-2xl mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-canopy/8 border border-canopy/20 mb-4">
            <span className="w-1.5 h-1.5 rounded-full bg-canopy" />
            <span className="text-canopy text-[10px] font-bold uppercase tracking-[0.35em]">A Two-Year Design</span>
          </div>
          <h2 className="section-headline mb-4">
            How the study works.
          </h2>
          <p className="text-umber/65 text-base md:text-lg leading-relaxed font-light">
            NTBI is built to measure change, not just presence — a baseline year, a year of analysis, and an exact replication, so the data shows not only what lives in these parks but how it shifts over time.
          </p>
        </div>

        {/* Asymmetric Steps */}
        <div className="space-y-6">
          {steps.map((step, i) => (
            <div
              key={step.label}
              className="how-card"
              style={{
                opacity: 1,
                transform: 'none',
                transition: 'opacity 0.7s cubic-bezier(0.22, 1, 0.36, 1), transform 0.7s cubic-bezier(0.22, 1, 0.36, 1)',
              }}>
              
              <div
                className={`grid lg:grid-cols-12 gap-0 rounded-2xl border border-canopy/12 overflow-hidden bg-white shadow-sm ${
                  i % 2 === 1 ? 'lg:[direction:rtl]' : ''
                }`}>
                
                {/* Image placeholder side */}
                <div
                  className={`lg:col-span-5 relative min-h-[240px] lg:min-h-[300px] flex items-end justify-start ${
                    i % 2 === 1 ? 'lg:[direction:ltr]' : ''
                  }`}
                  style={step.imageSrc ? {} : { background: step.gradientStyle }}>
                  {step.imageSrc ? (
                    <img
                      src={step.imageSrc}
                      alt={step.placeholderLabel}
                      className="absolute inset-0 w-full h-full object-cover object-center"
                      style={{ display: 'block' }}
                    />
                  ) : (
                    <div className="absolute inset-0 opacity-10"
                      style={{
                        backgroundImage: 'radial-gradient(circle at 20% 80%, rgba(255,255,255,0.15) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(255,255,255,0.1) 0%, transparent 50%)',
                      }} />
                  )}
                  <div className="relative p-5">
                    <p
                      className="text-[9px] leading-snug px-2 py-1 rounded"
                      style={{
                        fontFamily: 'monospace',
                        color: 'rgba(255,255,255,0.70)',
                        background: 'rgba(0,0,0,0.38)',
                      }}>
                      {step.placeholderLabel}
                    </p>
                  </div>
                </div>

                {/* Content side */}
                <div className={`lg:col-span-7 p-7 md:p-10 flex flex-col justify-center ${i % 2 === 1 ? 'lg:[direction:ltr]' : ''}`}>
                  <span className="text-canopy text-[10px] font-bold uppercase tracking-[0.4em] mb-3">{step.label}</span>
                  <h3
                    className="font-display font-bold text-umber leading-tight mb-4"
                    style={{ fontSize: 'clamp(1.3rem, 2.5vw, 1.9rem)' }}>
                    {step.title}
                  </h3>
                  <p className="text-umber/65 text-sm md:text-base leading-relaxed font-light">{step.body}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HowItWorksSection;